import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	TextField,
	Typography,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SnackbarContext from '../../store/snackbar-context';
import AuthContext from '../../store/auth-context';
import ConfirmationDialog from './ConfirmationDialog';
import { updateOlympicsDeclarations } from '../../api/content-api';
import { cleanUpHtml } from '../../utils/string-tools';

type DeclarationsEditorDialogProps = {
	open: boolean;
	title: string;
	declarations: any[] | null;
	onClose: () => void;
	onUpdated: (updated: any[]) => void;
};

type EditableDeclaration = {
	clientId: string;
	id?: number | null;
	declaration: string;
	required: boolean;
	shortTitle: string;
};

const ALLOWED_TAGS = new Set(['A', 'BR', 'STRONG', 'B', 'EM', 'I', 'U']);
const ALLOWED_ATTRS: Record<string, string[]> = {
	A: ['href', 'target', 'rel'],
};

const validateDeclarationHtml = (html: string) => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
	const wrapper = doc.body.firstElementChild;
	if (!wrapper) {
		return { valid: true, message: '' };
	}

	let valid = true;
	let message = '';

	const walk = (node: Node) => {
		if (!valid) return;
		if (node.nodeType === Node.TEXT_NODE) return;
		if (node.nodeType !== Node.ELEMENT_NODE) {
			valid = false;
			message = 'Dozwolone są tylko tagi HTML i tekst.';
			return;
		}
		const element = node as HTMLElement;
		const tag = element.tagName;
		if (!ALLOWED_TAGS.has(tag)) {
			valid = false;
			message = `Niedozwolony tag: <${tag.toLowerCase()}>`;
			return;
		}
		const allowedAttrs = ALLOWED_ATTRS[tag] || [];
		for (const attr of Array.from(element.attributes)) {
			if (!allowedAttrs.includes(attr.name)) {
				valid = false;
				message = `Niedozwolony atrybut: ${attr.name}`;
				return;
			}
		}
		if (tag === 'A') {
			const href = element.getAttribute('href');
			if (!href) {
				valid = false;
				message = 'Link musi mieć atrybut href.';
				return;
			}
		}
		for (const child of Array.from(element.childNodes)) {
			walk(child);
		}
	};

	for (const child of Array.from(wrapper.childNodes)) {
		walk(child);
	}

	return { valid, message };
};

const DeclarationsEditorDialog = ({ open, title, declarations, onClose, onUpdated }: DeclarationsEditorDialogProps) => {
	const { setMsg } = useContext(SnackbarContext);
	const { token } = useContext(AuthContext);
	const [items, setItems] = useState<EditableDeclaration[]>([]);
	const [isDirty, setIsDirty] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [confirmCloseText, setConfirmCloseText] = useState<string | null>(null);
	const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
	const [autoSaveStatus, setAutoSaveStatus] = useState<string | null>(null);
	const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const autoSaveStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!open || isDirty) {
			return;
		}
		const mapped = (declarations || []).map((declaration: any) => ({
			clientId: `declaration-${declaration.id}`,
			id: declaration.id,
			declaration: declaration.declaration || '',
			required: Boolean(declaration.required),
			shortTitle: declaration.short_title || '',
		}));
		setItems(mapped);
		setIsDirty(false);
	}, [open, isDirty, declarations]);

	const validation = useMemo(() => {
		return items.map((item) => {
			const hasText = item.declaration.trim().length > 0;
			const result = hasText ? validateDeclarationHtml(item.declaration) : { valid: false, message: 'Wpisz treść.' };
			return {
				valid: result.valid,
				message: result.message,
			};
		});
	}, [items]);

	const isValidList = useCallback(
		(list: EditableDeclaration[]) => {
			return list.every((item, index) => {
				if (!item.declaration.trim()) {
					return false;
				}
				return validation[index]?.valid;
			});
		},
		[validation],
	);

	const updateItem = (clientId: string, changes: Partial<EditableDeclaration>) => {
		setItems((prev) => prev.map((item) => (item.clientId === clientId ? { ...item, ...changes } : item)));
		setIsDirty(true);
		setAutoSaveStatus(null);
	};

	const requestRowSave = (row: EditableDeclaration, index: number) => {
		if (!autoSaveEnabled || isSaving) {
			return;
		}
		if (!row.declaration.trim()) {
			return;
		}
		if (validation[index]?.valid) {
			handleSave('auto');
		}
	};

	const addItem = () => {
		const newItem: EditableDeclaration = {
			clientId: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
			id: null,
			declaration: '',
			required: false,
			shortTitle: '',
		};
		setItems((prev) => [...prev, newItem]);
		setIsDirty(true);
		setAutoSaveStatus(null);
	};

	const removeItem = (clientId: string) => {
		setItems((prev) => prev.filter((item) => item.clientId !== clientId));
		setIsDirty(true);
		setAutoSaveStatus(null);
	};

	const handleSave = useCallback(
		async (source: 'auto' | 'manual') => {
			if (isSaving) {
				return;
			}
			if (!isValidList(items)) {
				setMsg({ msg: 'Uzupełnij poprawnie deklaracje przed zapisem' });
				return;
			}
			setIsSaving(true);
			const payload = items.map((item) => ({
				id: item.id,
				declaration: item.declaration.trim(),
				required: item.required,
				short_title: item.shortTitle ? item.shortTitle.trim() : null,
			}));
			try {
				const response = await updateOlympicsDeclarations(token, { declarations: payload });
				if (response.status === 200) {
					const updated = response.data || [];
					setItems(
						updated.map((declaration: any) => ({
							clientId: `declaration-${declaration.id}`,
							id: declaration.id,
							declaration: declaration.declaration || '',
							required: Boolean(declaration.required),
							shortTitle: declaration.short_title || '',
						})),
					);
					setIsDirty(false);
					if (source === 'auto') {
						setAutoSaveStatus('Automatycznie zapisano');
						if (autoSaveStatusTimer.current) {
							clearTimeout(autoSaveStatusTimer.current);
						}
						autoSaveStatusTimer.current = setTimeout(() => {
							setAutoSaveStatus(null);
						}, 3000);
					}
					onUpdated(updated);
				} else {
					setMsg({ msg: response.data });
				}
			} catch (exception: any) {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
			} finally {
				setIsSaving(false);
			}
		},
		[isSaving, isValidList, items, onUpdated, setMsg, token],
	);

	useEffect(() => {
		if (!open) {
			if (autoSaveTimer.current) {
				clearTimeout(autoSaveTimer.current);
			}
			if (autoSaveStatusTimer.current) {
				clearTimeout(autoSaveStatusTimer.current);
			}
			return;
		}
		if (!autoSaveEnabled || !isDirty || isSaving) {
			return;
		}
		if (!isValidList(items)) {
			return;
		}
		if (autoSaveTimer.current) {
			clearTimeout(autoSaveTimer.current);
		}
		autoSaveTimer.current = setTimeout(() => {
			handleSave('auto');
		}, 5000);

		return () => {
			if (autoSaveTimer.current) {
				clearTimeout(autoSaveTimer.current);
			}
		};
	}, [autoSaveEnabled, handleSave, isDirty, isSaving, isValidList, items, open]);

	const handleCloseRequest = () => {
		if (isDirty || isSaving) {
			setConfirmCloseText('Masz niezapisane zmiany. Czy chcesz zamknąć okno bez zapisu?');
			return;
		}
		onClose();
	};

	const confirmClose = () => {
		setConfirmCloseText(null);
		onClose();
	};

	return (
		<>
			<Dialog
				open={open}
				fullWidth
				maxWidth='md'
				onClose={handleCloseRequest}
				PaperProps={{ sx: { minWidth: '860px' } }}
			>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
						<Typography
							variant='caption'
							color='text.secondary'
						>
							Dozwolone tagi: &lt;a&gt;, &lt;br&gt;, &lt;strong&gt;/&lt;b&gt;, &lt;em&gt;/&lt;i&gt;, &lt;u&gt;
						</Typography>
						{items.length === 0 && <Typography variant='body2'>Brak deklaracji na liście.</Typography>}
						{items.map((item, index) => {
							const validationItem = validation[index];
							const showPreview = Boolean(item.declaration.trim()) && validationItem?.valid;
							return (
								<Box
									key={item.clientId}
									sx={{
										display: 'grid',
										gridTemplateColumns: '1fr 220px',
										gap: 2,
										alignItems: 'start',
										borderBottom: '1px solid',
										borderColor: 'divider',
										pb: 2,
									}}
								>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
										<TextField
											label='Treść deklaracji'
											value={item.declaration}
											onChange={(event) => updateItem(item.clientId, { declaration: event.target.value })}
											onBlur={() => requestRowSave(item, index)}
											error={!validationItem?.valid}
											helperText={validationItem?.valid ? undefined : validationItem?.message}
											FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
											size='small'
											multiline
											minRows={3}
										/>
										{showPreview && (
											<Box
												sx={{
													position: 'relative',
													border: '1px solid',
													borderColor: 'divider',
													borderRadius: 1,
													px: 1.5,
													pb: 1,
													pt: 1.5,
												}}
											>
												<Typography
													variant='caption'
													color='text.secondary'
													sx={{
														position: 'absolute',
														top: -8,
														left: 12,
														display: 'inline-block',
														px: 0.5,
														backgroundColor: 'background.paper',
														fontSize: '0.75rem',
														lineHeight: 1.66,
														letterSpacing: '0.00938em',
													}}
												>
													Podgląd
												</Typography>
												<Typography
													variant='body2'
													component='div'
													dangerouslySetInnerHTML={{ __html: cleanUpHtml(item.declaration) }}
												/>
											</Box>
										)}
									</Box>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
										<TextField
											label='Tytuł skrócony'
											value={item.shortTitle}
											onChange={(event) => updateItem(item.clientId, { shortTitle: event.target.value })}
											onBlur={() => requestRowSave(item, index)}
											size='small'
										/>
										<FormControlLabel
											control={
												<Checkbox
													checked={item.required}
													onChange={(event) => updateItem(item.clientId, { required: event.target.checked })}
												/>
											}
											label='Wymagana'
										/>
										<IconButton
											aria-label='Usuń deklarację'
											onClick={() => removeItem(item.clientId)}
											sx={{ alignSelf: 'flex-start' }}
										>
											<DeleteOutlineIcon fontSize='small' />
										</IconButton>
									</Box>
								</Box>
							);
						})}
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Button
							startIcon={<AddIcon />}
							variant='outlined'
							onClick={addItem}
						>
							Dodaj deklarację
						</Button>
						<FormControlLabel
							control={
								<Checkbox
									checked={autoSaveEnabled}
									onChange={(event) => setAutoSaveEnabled(event.target.checked)}
								/>
							}
							label='Autozapis'
						/>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						{autoSaveStatus && <Typography variant='caption'>{autoSaveStatus}</Typography>}
						{isDirty && <Typography variant='caption'>Niezapisane zmiany</Typography>}
						<Button
							variant='outlined'
							onClick={handleCloseRequest}
						>
							Zamknij
						</Button>
						<Button
							startIcon={<SaveIcon />}
							variant='contained'
							onClick={() => handleSave('manual')}
							disabled={!isDirty || !isValidList(items) || isSaving}
							sx={{ minWidth: '150px' }}
						>
							{isSaving ? 'Zapisywanie...' : 'Zapisz'}
						</Button>
					</Box>
				</DialogActions>
			</Dialog>
			<ConfirmationDialog
				close={() => setConfirmCloseText(null)}
				inProgress={false}
				text={confirmCloseText}
				onSuccess={confirmClose}
			/>
		</>
	);
};

export default DeclarationsEditorDialog;
